/** @format */
import { StateService, Transition } from '@uirouter/core';
import { CourseUnit, CustomCourseUnitAttainment } from 'common-typescript/types';
import { combineLatestWith, firstValueFrom } from 'rxjs';
import {
    AppErrorHandler,
    PlanEntityService,
    SisStateDeclaration,
    StudyRightEntityService,
    UniversityService,
    UserSettingsEntityService,
} from 'sis-components';
import { AddToPlanWizardComponent } from './add-to-plan-wizard/add-to-plan-wizard.component';
import { CreateFirstPlanComponent } from './create-first-plan/create-first-plan.component';
import { PlanNgComponent } from './plan-ng.component';
import { STUDENT_ADD_TO_PLAN, STUDENT_PLAN, STUDENT_PLAN_CREATE_FIRST_PLAN, STUDENT_PLAN_STRUCTURE } from './plan-ng.route.constant';
import { PlanRedirectionService } from './plan-redirection.service';

const handlePlanRedirection = async (transition: Transition) => {
    await redirectToOldPlanVersionIfNeeded(transition);
    const planEntityService: PlanEntityService = transition.injector().get(PlanEntityService) as PlanEntityService;
    const studyRightEntityService: StudyRightEntityService = transition.injector().get(StudyRightEntityService) as StudyRightEntityService;
    const appErrorHandler = transition.injector().get(AppErrorHandler) as AppErrorHandler;
    const planRedirectionService = transition.injector().get(PlanRedirectionService) as PlanRedirectionService;

    const [studentPlans, studentStudyRights] = await firstValueFrom(
        planEntityService
            .getMyPlans()
            .pipe(combineLatestWith(studyRightEntityService.getStudyRightsForCurrentUser()), appErrorHandler.defaultErrorHandler()),
    );

    return planRedirectionService.resolvePlanRedirectionState(studentPlans, studentStudyRights);
};

const checkIfOldPlanVersionInUse = async (transition: Transition) => {
    await redirectToOldPlanVersionIfNeeded(transition);
};

const redirectToOldPlanVersionIfNeeded = async (transition: Transition) => {
    const userSettingsService = transition.injector().get(UserSettingsEntityService) as UserSettingsEntityService;
    const universityService = transition.injector().get(UniversityService) as UniversityService;
    const stateService = transition.injector().get(StateService) as StateService;
    const userSettings = await firstValueFrom(userSettingsService.getOwnSettings());
    const universitySettings = await firstValueFrom(universityService.getCurrentUniversitySettings());
    const isNewPlanVersionInUse = userSettings?.isNewPlanVersionInUse;
    const forceNewPlanActive = universitySettings?.frontendFeatureToggles?.forceNewPlanActive;

    // If user has already selected to use old plan version and is not forced to new plan, redirect to old view
    if (isNewPlanVersionInUse === false && !forceNewPlanActive) {
        stateService.go('student.logged-in.planSelect');
    } else if (forceNewPlanActive && !isNewPlanVersionInUse) {
        await firstValueFrom(userSettingsService.saveOwnSettings({ ...userSettings, isNewPlanVersionInUse: true }));
    }
};

export const planStates: SisStateDeclaration[] = [
    {
        name: STUDENT_PLAN,
        url: '/new-plan',
        headerParams: {
            skipBreadcrumb: true,
        },
        redirectTo: handlePlanRedirection,
    },
    {
        name: STUDENT_PLAN_CREATE_FIRST_PLAN,
        url: '/create-first-plan',
        data: {
            browserTabTitle: 'APPLICATION_STRUCTURE_OF_STUDIES',
            hideLastNode: false,
            showHome: true,
        },
        headerParams: {
            displayNameKey: 'APPLICATION_STRUCTURE_OF_STUDIES',
            skipBreadcrumb: false,
        },
        redirectTo: checkIfOldPlanVersionInUse,
        component: CreateFirstPlanComponent,
    },
    {
        name: STUDENT_PLAN_STRUCTURE,
        dynamic: true,
        url: '/:planId',
        data: {
            browserTabTitle: 'APPLICATION_STRUCTURE_OF_STUDIES',
            hideLastNode: false,
            showHome: true,
        },
        headerParams: {
            displayNameKey: 'APPLICATION_STRUCTURE_OF_STUDIES',
            skipBreadcrumb: false,
        },
        resolve: [
            {
                token: 'planId',
                deps: [Transition],
                resolveFn: (transition: Transition) => transition.params().planId,
            },
        ],
        redirectTo: checkIfOldPlanVersionInUse,
        component: PlanNgComponent,
    },
    {
        name: STUDENT_ADD_TO_PLAN,
        url: '/add-to-plan',
        headerParams: {
            skipBreadcrumb: true,
        },
        focusMode: {
            defaultExitState: STUDENT_PLAN,
            titleKey: 'ADD_TO_PLAN_WIZARD.TITLE',
        },
        resolve: {
            courseUnit: courseUnitResolver,
            planId: planIdResolver,
            customCourseUnitAttainment: customCourseUnitAttainmentResolver,
        },
        component: AddToPlanWizardComponent,
    },
];

export function courseUnitResolver($transition$: Transition): Promise<CourseUnit | undefined> {
    return $transition$.options().custom.courseUnit;
}
courseUnitResolver.$inject = ['$transition$'];

export function customCourseUnitAttainmentResolver($transition$: Transition): Promise<CustomCourseUnitAttainment | undefined> {
    return $transition$.options().custom.customCourseUnitAttainment;
}
customCourseUnitAttainmentResolver.$inject = ['$transition$'];

export async function planIdResolver($transition$: Transition) {
    if ($transition$.options().custom.planId) {
        return $transition$.options().custom.planId;
    }
    const planEntityService: PlanEntityService = $transition$.injector().get(PlanEntityService) as PlanEntityService;

    const studentPlans = await firstValueFrom(planEntityService.getMyPlans());
    return studentPlans.length > 0 ? studentPlans[0].id : null;
}
planIdResolver.$inject = ['$transition$'];
